import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { Trophy, ChevronRight, Calendar, MapPin, Star, Target, Users, Award, Filter } from 'lucide-react';

const API_BASE = '';
const LOCALE_MAP = { tr: 'tr-TR', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-BR', it: 'it-IT', nl: 'nl-NL' };

export default function CupDetailPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fixtures');
  const [roundFilter, setRoundFilter] = useState('');
  const isTR = currentLang === 'tr';
  const locale = LOCALE_MAP[currentLang] || 'en-US';

  useEffect(() => {
    if (!slug) return;
    // Redirect WC slug to dedicated WC hub
    if (slug === 'world-cup-2026') {
      const wcPath = currentLang === 'tr' ? 'dunya-kupasi-2026' : 'world-cup-2026';
      window.location.replace(`/${currentLang}/${wcPath}`);
      return;
    }
    setLoading(true);
    fetch(`${API_BASE}/api/cups/${slug}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, currentLang]);

  const cup = data?.cup;
  const fixtures = data?.fixtures || [];
  const standings = data?.standings || [];
  const rounds = data?.rounds || [];
  const top_scorers = data?.top_scorers || [];
  const stats = data?.stats || { total_matches: 0, played: 0, upcoming: 0, total_goals: 0 };
  const hasStandings = standings.length > 0;
  const hasScorers = top_scorers.length > 0;

  const filteredFixtures = useMemo(() => {
    if (!roundFilter) return fixtures;
    return fixtures.filter(f => f.round === roundFilter);
  }, [fixtures, roundFilter]);

  const fixturesByRound = useMemo(() => {
    const groups = {};
    for (const f of filteredFixtures) {
      const key = f.round || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(f);
    }
    return groups;
  }, [filteredFixtures]);

  if (loading) return <LoadingPage />;
  if (!cup || data.error) return (
    <div className="container mx-auto px-4 py-16 text-center">
      <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
      <p className="text-slate-400">{t('cups.notFound', 'Cup not found')}</p>
    </div>
  );

  const tabs = [
    { key: 'fixtures', label: t('cups.fixtures', 'Fixtures'), icon: Calendar },
    ...(hasStandings ? [{ key: 'standings', label: t('cups.standings', 'Standings'), icon: Trophy }] : []),
    ...(hasScorers ? [{ key: 'scorers', label: t('cups.topScorers', 'Top Scorers'), icon: Target }] : []),
  ];

  return (
    <div data-testid="cup-detail-page">
      <SEOHead title={`${cup.name} | ODDSIS`} description={`${cup.name} - ${isTR ? 'Fikstur, puan durumu, gol kralligi' : 'Fixtures, standings, top scorers'}`} />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: cup.tier === 'elite' ? 'linear-gradient(135deg, #020617 0%, #1a0a2e 30%, #312e81 60%, #020617 100%)' : 'linear-gradient(135deg, #020617 0%, #0a1628 40%, #1e3a5f 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(245,158,11,0.2) 0%, transparent 50%)' }} />
        </div>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-8 md:py-12">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
            <Link to={`/${currentLang}`} className="hover:text-slate-200">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/${currentLang}/${isTR ? 'kupalar' : 'cups'}`} className="hover:text-amber-300 text-amber-400/70">{t('cups.cupsNav', 'Cups')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{cup.name}</span>
          </div>

          <div className="flex items-center gap-5">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white border border-white/20 flex items-center justify-center shrink-0">
              <img src={cup.logo_url} alt={cup.name} className="w-14 h-14 md:w-16 md:h-16 object-contain" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                {cup.tier === 'elite' && <Star className="w-5 h-5 text-amber-400 fill-amber-400/30" />}
                <h1 className="text-2xl md:text-4xl font-black text-white tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                  {cup.name}
                </h1>
              </div>
              <p className="text-sm text-slate-400">{cup.country} - {cup.season}</p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { val: stats.total_matches, label: t('cups.matches', 'Matches'), icon: Calendar },
              { val: stats.played, label: t('cups.played', 'Played'), icon: Award },
              { val: stats.upcoming, label: t('cups.upcoming', 'Upcoming'), icon: Users },
              { val: stats.total_goals, label: t('cups.goals', 'Goals'), icon: Target },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2">
                <s.icon className="w-4 h-4 text-amber-400/60" />
                <span className="text-base font-bold text-white tabular-nums">{s.val}</span>
                <span className="text-xs text-slate-400">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-16 z-30 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex gap-1 py-2">
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === tab.key
                    ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                    : 'text-slate-500 hover:text-slate-300 border border-transparent'
                }`}>
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-6">
        {/* FIXTURES TAB */}
        {activeTab === 'fixtures' && (
          <div data-testid="fixtures-tab">
            {/* Round Filter */}
            {rounds.length > 1 && (
              <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-2">
                <Filter className="w-4 h-4 text-slate-500 shrink-0" />
                <button onClick={() => setRoundFilter('')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${!roundFilter ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300 border border-transparent'}`}>
                  {t('cups.all', 'All')}
                </button>
                {rounds.map(r => (
                  <button key={r} onClick={() => setRoundFilter(r)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${roundFilter === r ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-slate-800/40 text-slate-500 hover:text-slate-300 border border-transparent'}`}>
                    {r}
                  </button>
                ))}
              </div>
            )}

            {/* Fixtures by Round */}
            {Object.entries(fixturesByRound).map(([round, matches]) => (
              <div key={round} className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-1 h-6 bg-amber-500 rounded-full" />
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">{round}</h3>
                  <span className="text-[10px] text-slate-600">{matches.length} {t('cups.matches', 'matches')}</span>
                </div>
                <div className="space-y-1.5">
                  {matches.map(m => <FixtureRow key={m.id} match={m} locale={locale} />)}
                </div>
              </div>
            ))}
            {fixtures.length === 0 && (
              <div className="text-center py-16 text-slate-500">
                <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-700" />
                <p>{t('cups.notFound', 'Fixtures not yet published')}</p>
              </div>
            )}
          </div>
        )}

        {/* STANDINGS TAB */}
        {activeTab === 'standings' && hasStandings && (
          <div className="space-y-6" data-testid="standings-tab">
            {standings.map((group, gi) => (
              <div key={gi} className="bg-slate-900/60 border border-slate-800/50 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/50 bg-slate-900/80">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">{group.name}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 border-b border-slate-800/30">
                        <th className="text-left px-4 py-2 w-8">#</th>
                        <th className="text-left px-4 py-2">{t('wc.team', 'Team')}</th>
                        <th className="text-center px-2 py-2">{t('wc.p', 'P')}</th>
                        <th className="text-center px-2 py-2">{t('wc.w', 'W')}</th>
                        <th className="text-center px-2 py-2">{t('wc.d', 'D')}</th>
                        <th className="text-center px-2 py-2">{t('wc.l', 'L')}</th>
                        <th className="text-center px-2 py-2">{t('wc.gf', 'GF')}</th>
                        <th className="text-center px-2 py-2">{t('wc.ga', 'GA')}</th>
                        <th className="text-center px-2 py-2">{t('wc.gd', 'GD')}</th>
                        <th className="text-center px-2 py-2 font-bold">{t('wc.pts', 'Pts')}</th>
                        <th className="text-center px-2 py-2">Form</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.teams.map((team, i) => (
                        <tr key={i} className={`border-b border-slate-800/20 ${i < 2 ? 'bg-emerald-500/5' : i >= group.teams.length - 1 ? 'bg-red-500/5' : ''}`}>
                          <td className="px-4 py-2.5 text-slate-500 font-medium">{team.rank || i + 1}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <img src={team.team_logo} alt="" className="w-5 h-5 rounded" />
                              <span className="text-slate-200 font-medium text-sm">{team.team_name}</span>
                            </div>
                          </td>
                          <td className="text-center px-2 py-2.5 text-slate-400">{team.played}</td>
                          <td className="text-center px-2 py-2.5 text-emerald-400">{team.won}</td>
                          <td className="text-center px-2 py-2.5 text-slate-400">{team.drawn}</td>
                          <td className="text-center px-2 py-2.5 text-rose-400">{team.lost}</td>
                          <td className="text-center px-2 py-2.5 text-slate-400">{team.gf}</td>
                          <td className="text-center px-2 py-2.5 text-slate-400">{team.ga}</td>
                          <td className="text-center px-2 py-2.5 text-slate-400">{team.gd}</td>
                          <td className="text-center px-2 py-2.5 font-bold text-white">{team.points}</td>
                          <td className="text-center px-2 py-2.5">
                            <div className="flex gap-0.5 justify-center">
                              {(team.form || '').split('').slice(-5).map((f, fi) => (
                                <span key={fi} className={`w-4 h-4 rounded-sm text-[9px] font-bold flex items-center justify-center ${
                                  f === 'W' ? 'bg-emerald-500/20 text-emerald-400' : f === 'D' ? 'bg-amber-500/20 text-amber-400' : f === 'L' ? 'bg-red-500/20 text-red-400' : 'bg-slate-800 text-slate-600'
                                }`}>{f}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TOP SCORERS TAB */}
        {activeTab === 'scorers' && hasScorers && (
          <div className="bg-slate-900/60 border border-slate-800/50 rounded-xl overflow-hidden" data-testid="scorers-tab">
            <div className="p-4 border-b border-slate-800/50">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
                <Target className="w-4 h-4 text-amber-400" />{t('cups.topScorers', 'Top Scorers')}
              </h3>
            </div>
            <div className="divide-y divide-slate-800/30">
              {top_scorers.map((p, i) => (
                <div key={i} className={`flex items-center gap-4 px-5 py-3.5 ${i === 0 ? 'bg-amber-500/5' : i < 3 ? 'bg-slate-800/20' : ''}`}>
                  <span className={`w-8 text-center font-black text-lg tabular-nums ${i === 0 ? 'text-amber-400' : i < 3 ? 'text-slate-300' : 'text-slate-600'}`} style={{ fontFamily: 'var(--font-heading)' }}>
                    {i + 1}
                  </span>
                  <img src={p.photo} alt="" className="w-10 h-10 rounded-full bg-slate-800 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${i === 0 ? 'text-amber-300' : 'text-white'}`}>{p.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <img src={p.team_logo} alt="" className="w-4 h-4 rounded" />
                      <span className="text-xs text-slate-500">{p.team}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-center">
                      <p className={`text-xl font-black tabular-nums ${i === 0 ? 'text-amber-400' : 'text-white'}`} style={{ fontFamily: 'var(--font-heading)' }}>{p.goals}</p>
                      <p className="text-[9px] text-slate-500 uppercase">{t('cups.goals', 'Goals')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-bold text-slate-400 tabular-nums">{p.assists || 0}</p>
                      <p className="text-[9px] text-slate-500 uppercase">{t('cups.assists', 'Assists')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-500 tabular-nums">{p.appearances}</p>
                      <p className="text-[9px] text-slate-600 uppercase">{t('cups.apps', 'Apps')}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FixtureRow({ match: m, locale }) {
  const date = m.date ? new Date(m.date) : null;
  const dateStr = date ? date.toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '';
  const timeStr = date ? date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '';
  const isFinished = m.status === 'Match Finished';
  const isLive = ['1H', '2H', 'HT', 'ET', 'P'].includes(m.status_short);

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all hover:bg-slate-800/30 ${
      isLive ? 'border-red-500/30 bg-red-500/5' : isFinished ? 'border-slate-800/40 bg-slate-900/30' : 'border-slate-800/40 bg-slate-900/20'
    }`} data-testid={`fixture-${m.id}`}>
      <div className="w-16 text-center shrink-0">
        {isLive ? (
          <span className="text-[10px] font-bold text-red-400 bg-red-500/15 px-2 py-0.5 rounded animate-pulse">LIVE</span>
        ) : isFinished ? (
          <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">FT</span>
        ) : (
          <>
            <p className="text-xs text-slate-400 font-medium">{dateStr}</p>
            <p className="text-[10px] text-slate-500">{timeStr}</p>
          </>
        )}
      </div>

      <div className="flex items-center gap-2.5 flex-1 justify-end text-right">
        <span className={`text-sm font-medium ${isFinished && m.score_home > m.score_away ? 'text-white' : 'text-slate-300'}`}>{m.home_name}</span>
        <img src={m.home_logo} alt="" className="w-6 h-6 rounded" />
      </div>

      <div className="w-16 text-center shrink-0">
        {m.score_home != null ? (
          <span className={`text-base font-black tabular-nums px-2.5 py-0.5 rounded-lg ${isLive ? 'text-red-400 bg-red-500/10' : 'text-amber-400 bg-slate-800'}`}>
            {m.score_home} - {m.score_away}
          </span>
        ) : (
          <span className="text-xs text-slate-600 font-bold">VS</span>
        )}
      </div>

      <div className="flex items-center gap-2.5 flex-1">
        <img src={m.away_logo} alt="" className="w-6 h-6 rounded" />
        <span className={`text-sm font-medium ${isFinished && m.score_away > m.score_home ? 'text-white' : 'text-slate-300'}`}>{m.away_name}</span>
      </div>

      {m.venue && (
        <div className="hidden md:flex items-center gap-1 text-[10px] text-slate-600 w-36 shrink-0 justify-end">
          <MapPin className="w-2.5 h-2.5" />{m.venue}
        </div>
      )}
    </div>
  );
}
